import {
  DefaultApi,
  DeleteDatabricksAppInfoRequest,
  UpsertDatabricksAppInfoRequest,
} from "@coeff/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import Ajv, { JSONSchemaType } from "ajv";
import { useForm } from "antd/lib/form/Form";
import * as React from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { AppState } from "src/store";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  AlertExclamation,
  Button,
  Checkbox,
  FlexRow,
  Form,
  FormItem,
  Input,
  LoaderWithPerfTimings,
  message,
  Modal,
  OAuthActions,
  OAuthConfiguredAlert,
  OAuthDeleteButton,
  SettingsLayout,
  Typography,
} from "../components";
import { COLORS } from "../constants";
import { getBaseUrl, sentryCapture, useApiContext, useTrack } from "../utils";

const HelperText = styled.div`
  color: ${COLORS.black45};
  margin-bottom: 20px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 40px -15px -10px -15px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 20px;
`;

const StyledAlert = styled(Alert)<{ $background?: string; $marginBottom?: string }>`
  line-height: 1.8em;
  padding: 12px;

  code {
    white-space: nowrap;
    background-color: ${COLORS.black4};
    padding: 4px 6px;
  }

  .ant-alert-icon {
    margin: 6px;
  }

  ${({ $background }) => ($background ? `background-color: ${$background};` : "")}
  ${({ $marginBottom }) => ($marginBottom ? `margin-bottom: ${$marginBottom};` : "")}
`;

const StyledModal = styled(Modal)`
  &.ant-modal {
    .ant-modal-body {
      padding: 5px;
    }
  }
`;

const ajv = new Ajv();
const schema: JSONSchemaType<UpsertDatabricksAppInfoRequest> = {
  type: "object",
  properties: {
    issuer_uri: { type: "string" },
    client_id: { type: "string" },
    client_secret: { type: "string" },
    domain: { type: "string", nullable: true },
  },
  required: ["issuer_uri", "client_id", "client_secret"],
};

const validate = ajv.compile(schema);

const fetchDatabricksAppInfo = async (api: DefaultApi) => {
  const response = await api.listDatabricksAppInfo();
  return response?.data?.apps_list;
};

const deleteDatabricksAppInfo = async (
  api: DefaultApi,
  deleteRequest: DeleteDatabricksAppInfoRequest
) => {
  await api.deleteDatabricksAppInfo({ delete_databricks_app_info_request: deleteRequest });
};

export function normalizeIssuerUri(input: string): {
  correctedIssuerUri: string;
  issuer_type?: "azure" | "native_account" | "native_workspace";
  status: "valid" | "error";
  error_message?: string;
} {
  const trimmed = input.trim();

  // Azure format
  const azureMatch = trimmed.match(
    /^https:\/\/login\.microsoftonline\.com\/([a-fA-F0-9-]{36})\/?$/
  );
  if (azureMatch) {
    return {
      correctedIssuerUri: `https://login.microsoftonline.com/${azureMatch[1]}`,
      issuer_type: "azure",
      status: "valid",
    };
  }

  // Native account-level
  const nativeAccountMatch = trimmed.match(
    /^https:\/\/accounts\.cloud\.databricks\.com\/oidc\/accounts\/([a-fA-F0-9-]{36})\/?$/
  );
  if (nativeAccountMatch) {
    return {
      correctedIssuerUri: `https://accounts.cloud.databricks.com/oidc/accounts/${nativeAccountMatch[1]}`,
      issuer_type: "native_account",
      status: "valid",
    };
  }

  // Fix common mistake: workspace listing URL
  const accountIdParamMatch = trimmed.match(
    /^https:\/\/accounts\.cloud\.databricks\.com\/workspaces\?account_id=([a-fA-F0-9-]{36})/
  );
  if (accountIdParamMatch) {
    return {
      correctedIssuerUri: `https://accounts.cloud.databricks.com/oidc/accounts/${accountIdParamMatch[1]}`,
      issuer_type: "native_account",
      status: "valid",
    };
  }

  // Workspace-level URI without /oidc
  const workspaceHostMatch = trimmed.match(/^https:\/\/([a-z0-9-]+)\.cloud\.databricks\.com\/?$/);
  if (workspaceHostMatch) {
    return {
      correctedIssuerUri: `https://${workspaceHostMatch[1]}.cloud.databricks.com/oidc`,
      issuer_type: "native_workspace",
      status: "valid",
    };
  }

  // Workspace-level URI with /oidc
  const workspaceOidcMatch = trimmed.match(
    /^https:\/\/([a-z0-9-]+)\.cloud\.databricks\.com\/oidc\/?$/
  );
  if (workspaceOidcMatch) {
    return {
      correctedIssuerUri: trimmed.replace(/\/+$/, ""),
      issuer_type: "native_workspace",
      status: "valid",
    };
  }

  // Azure Databricks workspace with /oidc (e.g., https://adb-xxx.1.azuredatabricks.net/oidc)
  const azureWorkspaceOidcMatch = trimmed.match(
    /^https:\/\/(adb-\d+\.\d+)\.azuredatabricks\.net\/oidc\/?$/
  );
  if (azureWorkspaceOidcMatch) {
    return {
      correctedIssuerUri: trimmed.replace(/\/+$/, ""),
      issuer_type: "native_workspace",
      status: "valid",
    };
  }

  // Azure Databricks workspace without /oidc (e.g., https://adb-xxx.1.azuredatabricks.net)
  const azureWorkspaceMatch = trimmed.match(/^https:\/\/(adb-\d+\.\d+)\.azuredatabricks\.net\/?$/);
  if (azureWorkspaceMatch) {
    return {
      correctedIssuerUri: `https://${azureWorkspaceMatch[1]}.azuredatabricks.net/oidc`,
      issuer_type: "native_workspace",
      status: "valid",
    };
  }

  return {
    correctedIssuerUri: trimmed,
    status: "error",
    error_message:
      "Could not determine a valid Azure or Databricks issuer URI from the provided input.",
  };
}

export const DatabricksOAuthSettings = () => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = React.useState(false);
  const [form] = useForm<UpsertDatabricksAppInfoRequest>();
  const [errorMessage, setErrorMessage] = React.useState("");

  const track = useTrack();
  const history = useHistory();
  const location = useLocation();
  const redirectOnAdd = new URLSearchParams(location.search).get("back_on_add");

  const isEnabled = !clientFlags?.is_gmail_like_domain;

  React.useEffect(() => {
    document.title = "Databricks OAuth Settings - Coefficient Workspace";
    track("workspace_databricks_oauth_settings_viewed");
  }, []);

  const { apiClient: api } = useApiContext();

  const {
    data: databricksAppInfo,
    refetch,
    isLoading,
  } = useQuery(
    [QueryCacheKey.DATABRICKS_APP_INFO, location.search],
    async () => {
      const apps = await fetchDatabricksAppInfo(api);
      return apps?.[0];
    },
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: isEnabled,
      onSuccess: data => {
        if (data) {
          form.setFieldsValue({
            issuer_uri: data.issuer_uri,
            client_id: data.client_id,
          });
        }
      },
    }
  );

  const checkConnection = (data: UpsertDatabricksAppInfoRequest) => {
    const originalIssuerUri = data.issuer_uri?.trim() ?? "";
    const { correctedIssuerUri, status, error_message } = normalizeIssuerUri(originalIssuerUri);

    if (status === "error") {
      const error = {
        title: "Invalid Issuer URI",
        message: error_message || "The provided issuer URI is invalid.",
      };
      setErrorMessage(`${error.title}: ${error.message}`);
      throw new Error(error.message);
    }

    if (correctedIssuerUri !== originalIssuerUri) {
      message.info("The issuer URI was automatically corrected.");
    }

    upsertAppInfoMutation.mutate({
      ...data,
      issuer_uri: correctedIssuerUri,
    });
  };

  const upsertAppInfoMutation = useMutation(
    (json: UpsertDatabricksAppInfoRequest) =>
      api.upsertDatabricksAppInfo({
        upsert_databricks_app_info_request: {
          issuer_uri: json.issuer_uri,
          client_id: json.client_id,
          client_secret: json.client_secret,
        },
      }),
    {
      onSuccess: () => {
        message.success("Saved successfully");
        setShowSuccessModal(true);
        refetch();
        if (redirectOnAdd) {
          history.goBack();
        }
      },
    }
  );

  const deleteAppInfoMutation = useMutation(
    (deleteRequest: DeleteDatabricksAppInfoRequest) => deleteDatabricksAppInfo(api, deleteRequest),
    {
      onSuccess: () => {
        message.success("Deleted successfully from coefficient.");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
    }
  );

  if (!isEnabled) {
    return (
      <SettingsLayout title="Databricks OAuth Settings" dataSourceType="databricks" width="narrow">
        <Alert
          type="error"
          showIcon
          message="Databricks OAuth is not supported for your domain"
          description={`Databricks OAuth is not supported for ${
            clientFlags?.email.split("@")[1]
          }. Please use a different account to configure Databricks OAuth.`}
        />
      </SettingsLayout>
    );
  }

  const handleOnFinish = (data: UpsertDatabricksAppInfoRequest) => {
    const valid = validate(data);
    if (valid) {
      checkConnection(form.getFieldsValue(["issuer_uri", "client_id", "client_secret"]));
    }
  };

  return (
    <SettingsLayout title="Databricks OAuth Settings" dataSourceType="databricks" width="narrow">
      {errorMessage && (
        <StyledAlert
          showIcon
          type="error"
          icon={<AlertExclamation type="error" />}
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <Form
        layout="vertical"
        onFinish={handleOnFinish}
        onBeforeInput={() => setErrorMessage("")}
        initialValues={{
          issuer_uri: databricksAppInfo?.issuer_uri || "",
          client_id: databricksAppInfo?.client_id || "",
          client_secret: databricksAppInfo ? "abcdef" : "",
        }}
        disabled={
          Boolean(databricksAppInfo) ||
          isLoading ||
          upsertAppInfoMutation.isLoading ||
          deleteAppInfoMutation.isLoading
        }
        form={form}
      >
        {databricksAppInfo ? (
          <OAuthConfiguredAlert dataSourceType="databricks" $marginBottom="30px" />
        ) : (
          <HelperText>
            Allows users from your domain to connect to Databricks using OAuth.{" "}
            <a
              href="https://help.coefficient.io/hc/en-us/articles/27594448244251-Databricks"
              target="_blank"
              onClick={() =>
                track("help_center_clicked", {
                  event_from: "workspace_databricks_oauth_settings",
                  import_data_source: "databricks",
                })
              }
            >
              Help guide
            </a>
          </HelperText>
        )}

        <FormItem
          label="Issuer URI"
          name="issuer_uri"
          rules={[{ required: true, message: "Issuer URI is required" }]}
        >
          <Input
            placeholder="https://accounts.cloud.databricks.com/oidc/accounts/<account-id> or https://<workspace-instance-name>/oidc"
            title="https://accounts.cloud.databricks.com/oidc/accounts/<account-id> or https://<workspace-instance-name>/oidc"
          />
        </FormItem>

        {databricksAppInfo ? (
          <FormItem label="Client ID" name="client_id">
            <Input placeholder="Client ID" />
          </FormItem>
        ) : (
          <>
            <FormItem
              label="Client ID"
              name="client_id"
              rules={[{ required: true, message: "Client ID is required" }]}
            >
              <Input placeholder="Client ID" />
            </FormItem>
            <FormItem
              label="Client Secret"
              name="client_secret"
              rules={[{ required: true, message: "Client Secret is required" }]}
            >
              <Input type="password" placeholder="Client Secret" />
            </FormItem>
          </>
        )}

        {!databricksAppInfo && (
          <FlexRow gap={1}>
            {redirectOnAdd && (
              <Button
                type="text"
                onClick={() => history.goBack()}
                style={{ marginLeft: 10 }}
                disabled={isLoading}
              >
                Cancel
              </Button>
            )}
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Save
            </Button>
          </FlexRow>
        )}
      </Form>

      {databricksAppInfo && (
        <OAuthActions>
          <OAuthDeleteButton
            htmlType="button"
            onClick={() => {
              setDeleteConfirmed(false);
              setShowDeleteModal(true);
            }}
            loading={deleteAppInfoMutation.isLoading}
          />
        </OAuthActions>
      )}

      <StyledModal open={showSuccessModal} onCancel={() => setShowSuccessModal(false)}>
        <StyledAlert
          showIcon
          banner
          type="success"
          message="Saved successfully"
          description="Make sure Databricks OAuth is working properly by clicking 'Test connection' in the Coefficient Sidebar."
          $background="white"
        />
      </StyledModal>

      <Modal open={showDeleteModal} onCancel={() => setShowDeleteModal(false)}>
        <Typography fontWeight="bold" variant="h6" gutterBottom>
          Please Confirm
        </Typography>
        <Typography color="textSecondary">
          Are you sure you want to delete this Databricks OAuth configuration?
        </Typography>
        <StyledCheckbox
          onChange={() => setDeleteConfirmed(prev => !prev)}
          checked={deleteConfirmed}
        >
          Got it, please proceed
        </StyledCheckbox>
        <Footer>
          <Button
            type="text"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleteAppInfoMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            type="text"
            danger
            disabled={!deleteConfirmed || deleteAppInfoMutation.isLoading}
            onClick={() => {
              if (databricksAppInfo?.client_id && deleteConfirmed) {
                deleteAppInfoMutation.mutate({
                  client_id: databricksAppInfo.client_id,
                  domain: databricksAppInfo.domain,
                });
              }
              setShowDeleteModal(false);
            }}
          >
            Delete
          </Button>
        </Footer>
      </Modal>
    </SettingsLayout>
  );
};
